import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Stethoscope, ShieldAlert, Sparkles } from "lucide-react";
import { Reveal, SectionHeading, GlowOrb } from "@/components/landing/primitives";

const TRANSCRIPT =
  "« Respire un bon coup. Ce que tu décris, c'est classique : il te garde sous le coude sans vraiment s'engager. Mon conseil ? Arrête de relancer en premier pendant une semaine… Tu mérites quelqu'un qui te choisit vraiment. »";

function Waveform({ active }) {
  const bars = [8, 16, 26, 14, 22, 30, 12, 20, 28, 10, 18, 24, 14, 26, 9, 20, 30, 12, 22, 16];
  return (
    <div className="flex items-center gap-[3px] h-8" aria-hidden>
      {bars.map((h, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-pink-400"
          style={{
            height: `${h}px`,
            opacity: active ? 1 : 0.35,
            animation: active ? `wave 0.9s ease-in-out ${i * 0.05}s infinite alternate` : "none",
          }}
        />
      ))}
      <style>{`@keyframes wave { from { transform: scaleY(0.4); } to { transform: scaleY(1.1); } }`}</style>
    </div>
  );
}

function AudioTeaser() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setPlaying(false);
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  return (
    <div className="relative rounded-3xl bg-gradient-to-br from-pink-500/10 to-purple-600/10 border border-pink-500/25 p-6 md:p-8 overflow-hidden">
      <GlowOrb className="w-64 h-64 -top-20 -right-20" color="#EC4899" opacity={0.18} />
      <audio ref={audioRef} src="/media/docteur-teaser.mp3" preload="none" />
      <div className="relative flex items-center gap-4">
        <button
          data-testid="teaser-audio-play"
          onClick={toggle}
          className="shrink-0 w-16 h-16 rounded-full bg-pink-500 text-white flex items-center justify-center hover:bg-pink-600 transition-colors shadow-[0_0_28px_rgba(236,72,153,0.55)]"
          aria-label={playing ? "Pause" : "Écouter"}
        >
          {playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-pink-400 mb-1">
            <Stethoscope size={12} /> Voix du Docteur Love
          </div>
          <p className="text-sm font-semibold text-white">Écoute-le te répondre en vrai</p>
          <div className="mt-3"><Waveform active={playing} /></div>
        </div>
      </div>
      <p className="relative mt-5 text-sm text-zinc-300 leading-relaxed italic">{TRANSCRIPT}</p>
    </div>
  );
}

const CONVOS = [
  {
    app: "Tinder", who: "Match · Léo",
    bubbles: [
      { me: false, t: "Wsh tu fais quoi ce soir 👀" },
      { me: true, t: "Rien de prévu, pourquoi ?" },
      { me: false, t: "Chais pas on verra jvais surement sortir avec les potes" },
    ],
    tag: "Breadcrumbing", sev: "Moyen", score: 48, color: "#f59e0b",
    verdict: "Il te relance sans jamais rien proposer de concret. Ne réorganise pas ta soirée pour ces miettes.",
  },
  {
    app: "WhatsApp", who: "Camille 💕",
    bubbles: [
      { me: false, t: "T'es la personne la plus incroyable que j'ai rencontrée 🥹" },
      { me: false, t: "On se connaît depuis 4 jours mais je le sens" },
      { me: true, t: "Wow c'est rapide non ?" },
    ],
    tag: "Love bombing", sev: "Élevé", score: 35, color: "#ef4444",
    verdict: "Intensité disproportionnée beaucoup trop tôt. Vérifie que les actes suivent les mots avant de t'emballer.",
  },
];

function ConversationCard({ c, i }) {
  return (
    <Reveal delay={i * 0.1}>
      <div className="relative rounded-[2rem] border-4 border-zinc-800 bg-zinc-950 overflow-hidden shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-zinc-900/60">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 shrink-0" />
          <div>
            <p className="text-xs font-bold text-white leading-none">{c.who}</p>
            <p className="text-[9px] text-zinc-500 mt-0.5">{c.app}</p>
          </div>
        </div>
        <div className="p-4 space-y-2 min-h-[150px]">
          {c.bubbles.map((b, j) => (
            <div key={j} className={`flex ${b.me ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[13px] leading-snug ${
                b.me ? "bg-pink-500 text-white rounded-br-sm" : "bg-zinc-800 text-zinc-100 rounded-bl-sm"
              }`}>
                {b.t}
              </div>
            </div>
          ))}
        </div>
        <div className="m-3 mt-0 rounded-2xl bg-[#0d0d0f] border border-white/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-pink-300">
              <Sparkles size={11} /> Analyse DocLove
            </span>
            <span className="font-display text-lg font-black" style={{ color: c.color }}>{c.score}<span className="text-[9px] text-zinc-600">/100</span></span>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 mb-2 border"
            style={{ color: c.color, borderColor: `${c.color}55`, background: `${c.color}18` }}>
            🚩 {c.tag} · {c.sev}
          </span>
          <div className="flex items-start gap-1.5 text-[12px] text-zinc-400 leading-relaxed">
            <ShieldAlert size={13} className="shrink-0 mt-0.5" style={{ color: c.color }} />
            <span>{c.verdict}</span>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export function DemoShowcase() {
  return (
    <section className="relative py-20 md:py-28">
      <GlowOrb className="w-[420px] h-[420px] top-10 left-0" color="#a855f7" opacity={0.1} />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 relative">
        <Reveal>
          <SectionHeading
            eyebrow="En action"
            title="Vois (et écoute) DocLove à l'œuvre"
            sub="Des conversations décodées sans filtre, et ton coach qui te répond de vive voix."
            center
          />
        </Reveal>
        <div className="mt-12">
          <Reveal><AudioTeaser /></Reveal>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {CONVOS.map((c, i) => <ConversationCard key={c.tag} c={c} i={i} />)}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-center text-xs text-zinc-600">Exemples anonymisés à titre illustratif.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
